import { Reviewer } from "components/Submission/ReviewersEditList";
import { ReviewMode, SignatureType } from "generated-graphql/graphql";
import { SubmissionFormData, SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import { useCallback } from "react";
import { UseFormReturn } from "react-hook-form";

/** The two halves of "peer review implies review", kept together so neither can drift. */
function useReviewGateHandlers({
  disabled,
  reviewRequired,
  setReviewRequired,
  setPeerReviewRequired,
}: {
  disabled: boolean;
  reviewRequired: boolean;
  setReviewRequired: (next: boolean) => void;
  setPeerReviewRequired: (next: boolean) => void;
}) {
  // Neither toggle touches the reviewer list: it survives in local state so
  // toggling back before a save restores it. What a save actually writes is
  // derived in reviewersToPersist, and the dropped-approval confirm guards it.
  const handlePeerReviewToggle = useCallback(
    (next: boolean) => {
      if (disabled) return;
      setPeerReviewRequired(next);
      if (next && !reviewRequired) setReviewRequired(true);
    },
    [disabled, reviewRequired, setPeerReviewRequired, setReviewRequired]
  );

  // No review means no peer review, so the switch follows rather than sitting on
  // while the save writes it off.
  const handleReviewRequiredChange = useCallback(
    (next: boolean) => {
      if (disabled) return;
      setReviewRequired(next);
      if (!next) setPeerReviewRequired(false);
    },
    [disabled, setPeerReviewRequired, setReviewRequired]
  );

  return { handlePeerReviewToggle, handleReviewRequiredChange };
}

/** Edits to the signature type, reviewers, and signer, ahead of the signer/reviewer conflict confirmations. */
export function useReviewerConfigHandlers({
  disabled,
  reviewRequired,
  setReviewRequired,
  setSignatureType,
  setSigner,
  setPeerReviewRequired,
  reviewers,
  setReviewers,
  reviewModeForm,
  signer,
  setReviewerDialogOpen,
  setSignerDialogOpen,
  setReviewersToConfirm,
  setSignerToConfirm,
}: {
  disabled: boolean;
  reviewRequired: boolean;
  setReviewRequired: (next: boolean) => void;
  setSignatureType: (next: SignatureType) => void;
  setSigner: (next: SubmissionReviewerSigner | null) => void;
  setPeerReviewRequired: (next: boolean) => void;
  reviewers: Reviewer[];
  setReviewers: (update: Reviewer[] | ((prev: Reviewer[]) => Reviewer[])) => void;
  reviewModeForm: UseFormReturn<SubmissionFormData>;
  signer: SubmissionReviewerSigner | null;
  setReviewerDialogOpen: (next: boolean) => void;
  setSignerDialogOpen: (next: boolean) => void;
  setReviewersToConfirm: (next: { reviewers: Reviewer[]; reviewMode: ReviewMode } | null) => void;
  setSignerToConfirm: (next: SubmissionReviewerSigner | null) => void;
}) {
  const handleSignatureTypeChange = useCallback(
    (next: SignatureType) => {
      if (disabled) return;
      setSignatureType(next);
      // Clear the signer on type change; a compatible signer must be re-selected.
      setSigner(null);
    },
    [disabled, setSignatureType, setSigner]
  );

  const { handlePeerReviewToggle, handleReviewRequiredChange } = useReviewGateHandlers({
    disabled,
    reviewRequired,
    setReviewRequired,
    setPeerReviewRequired,
  });

  // Shared by the direct save and the signer-conflict confirm so both apply the
  // same state; the confirm path only adds clearing the signer.
  const applyReviewersSave = useCallback(
    (nextReviewers: Reviewer[], nextReviewMode: ReviewMode) => {
      setReviewers(nextReviewers);
      reviewModeForm.setValue("reviewMode", nextReviewMode, { shouldDirty: true });
      if (nextReviewers.length > 0 && !reviewRequired) setReviewRequired(true);
      setReviewerDialogOpen(false);
    },
    [reviewRequired, reviewModeForm, setReviewers, setReviewRequired, setReviewerDialogOpen]
  );

  const handleReviewersSave = useCallback(
    (nextReviewers: Reviewer[], nextReviewMode: ReviewMode) => {
      // One person can't be both a reviewer and the signer.
      if (signer && nextReviewers.some((r) => r.id === signer.id)) {
        setReviewersToConfirm({ reviewers: nextReviewers, reviewMode: nextReviewMode });
        return;
      }
      applyReviewersSave(nextReviewers, nextReviewMode);
    },
    [signer, applyReviewersSave, setReviewersToConfirm]
  );

  const handleSignerSave = useCallback(
    (nextSigner: SubmissionReviewerSigner) => {
      if (reviewers.some((r) => r.id === nextSigner.id)) {
        setSignerToConfirm(nextSigner);
        return;
      }
      setSigner(nextSigner);
      setSignerDialogOpen(false);
    },
    [reviewers, setSignerToConfirm, setSigner, setSignerDialogOpen]
  );

  const handleSaveReviewers = (
    nextReviewers: Reviewer[],
    nextReviewMode: ReviewMode,
    nextPeerReviewRequired?: boolean
  ) => {
    setPeerReviewRequired(nextPeerReviewRequired ?? false);
    handleReviewersSave(nextReviewers, nextReviewMode);
  };

  return {
    handleSignatureTypeChange,
    handlePeerReviewToggle,
    handleReviewRequiredChange,
    handleSignerSave,
    handleSaveReviewers,
    applyReviewersSave,
  };
}

/** Resolves the signer/reviewer mutual-exclusion confirmations: one person can't be both. */
export function useReviewerConfigConfirmHandlers({
  applyReviewersSave,
  setSigner,
  setReviewers,
  setSignerDialogOpen,
  reviewersToConfirm,
  setReviewersToConfirm,
  signerToConfirm,
  setSignerToConfirm,
}: {
  applyReviewersSave: (nextReviewers: Reviewer[], nextReviewMode: ReviewMode) => void;
  setSigner: (next: SubmissionReviewerSigner | null) => void;
  setReviewers: (update: Reviewer[] | ((prev: Reviewer[]) => Reviewer[])) => void;
  setSignerDialogOpen: (next: boolean) => void;
  reviewersToConfirm: { reviewers: Reviewer[]; reviewMode: ReviewMode } | null;
  setReviewersToConfirm: (next: { reviewers: Reviewer[]; reviewMode: ReviewMode } | null) => void;
  signerToConfirm: SubmissionReviewerSigner | null;
  setSignerToConfirm: (next: SubmissionReviewerSigner | null) => void;
}) {
  const handleConfirmReviewersToConfirm = () => {
    if (!reviewersToConfirm) return;
    setSigner(null);
    applyReviewersSave(reviewersToConfirm.reviewers, reviewersToConfirm.reviewMode);
    setReviewersToConfirm(null);
  };

  const handleConfirmSignerToConfirm = () => {
    if (!signerToConfirm) return;
    setReviewers((prev) => prev.filter((r) => r.id !== signerToConfirm.id));
    setSigner(signerToConfirm);
    setSignerToConfirm(null);
    setSignerDialogOpen(false);
  };

  return { handleConfirmReviewersToConfirm, handleConfirmSignerToConfirm };
}
